import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { HiTrash, HiTag } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import Services from '../../../services/Services';
import { ChannelUser } from '../../../types/service.types';
import Button from '../../../components/ui/Button/Button';
import FormInput from '../../../components/ui/Form/FormInput';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import Modal from '../../../components/ui/Modal/Modal';
import transitionStyles from '../../../styles/transitions.module.css';
import { Tabs, Tab } from '../../../components/ui/Tabs';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow, SortDirection } from '../../../components/ui/Table';
import { UserCard, UserProfileLink } from '../../../components/ui/UserDisplay';
import styles from './CommunityMembersPage.module.css';

import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';
import { useSegmentations } from '../../../hooks/useSegmentations';
import UserTagsDisplay from '../../../components/organizerPanel/UserTagsDisplay/UserTagsDisplay';
import TagDropdown from '../../../components/organizerPanel/TagDropdown/TagDropdown';

const CommunityMembersPage: React.FC = () => {
  const { channel } = useOutletContext<OrganizerPanelOutletContext>();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  const [activeTab, setActiveTab] = useState<'members' | 'requests'>('members');
  const [members, setMembers] = useState<ChannelUser[]>([]);
  const [pendingUsers, setPendingUsers] = useState<ChannelUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  // Modal states
  const [removeModal, setRemoveModal] = useState<{ isOpen: boolean; user: ChannelUser | null }>({ isOpen: false, user: null });
  const [adminModal, setAdminModal] = useState<{ isOpen: boolean; user: ChannelUser | null; action: 'assign' | 'revoke' }>({ isOpen: false, user: null, action: 'assign' });
  const [approveModal, setApproveModal] = useState<{ isOpen: boolean; user: ChannelUser | null }>({ isOpen: false, user: null });
  const [denyModal, setDenyModal] = useState<{ isOpen: boolean; user: ChannelUser | null }>({ isOpen: false, user: null });

  // Segmentation state
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [activeTagDropdown, setActiveTagDropdown] = useState<string | null>(null);
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [pendingBulkTags, setPendingBulkTags] = useState<Record<string, 'add' | 'remove'>>({});
  const tagDropdownAnchorRef = useRef<HTMLSpanElement>(null);

  // Segmentation hook
  const {
    segments,
    addSegment,
    addTagToUsers,
    removeTagFromUsers,
  } = useSegmentations({
    token,
    channelID: channel?.channelID,
  });

  const fetchMembers = async () => {
    if (!token || !channel?.channelID) return;
    
    try {
      const response = await Services.SocialHiveGetChannelUsers(token, channel.channelID);
      if (response.data.status && response.data.result) {
        setMembers(response.data.result);
      } else {
        setMembers([]);
      }
    } catch (error) {
      setMembers([]);
    }
  };

  const fetchPendingUsers = async () => {
    if (!token || !channel?.channelID) return;
    
    try {
      const response = await Services.SocialHiveGetChannelUserRequests(token, channel.channelID);
      if (response.data.status && response.data.result) {
        setPendingUsers(response.data.result);
      } else {
        setPendingUsers([]);
      }
    } catch (error) {
      setPendingUsers([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchMembers(), fetchPendingUsers()]);
      setLoading(false);
    };
    fetchData();
  }, [token, channel?.channelID]);

  const handleRemoveClick = (user: ChannelUser) => {
    if (user.isOwner) {
      toastError(getTranslation('organizerPanel.cannotRemoveOwner') || 'Topluluk sahibi kaldırılamaz');
      return;
    }
    setRemoveModal({ isOpen: true, user });
  };

  const handleRemoveConfirm = async () => {
    if (!removeModal.user || !token || !channel?.channelID) return;
    
    try {
      const response = await Services.SocialHiveRejectUserToPrivateChannel(
        token,
        channel.channelID,
        removeModal.user.userID || removeModal.user.id || ''
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.userRemoved') || 'Kullanıcı kaldırıldı');
        await fetchMembers();
        setRemoveModal({ isOpen: false, user: null });
      } else {
        toastError(getTranslation('organizerPanel.userRemoveError') || 'Kullanıcı kaldırılamadı');
      }
    } catch (error) {
      toastError(getTranslation('organizerPanel.userRemoveError') || 'Kullanıcı kaldırılamadı');
    }
  };

  const handleAdminToggle = (user: ChannelUser) => {
    if (user.isOwner) {
      toastError(getTranslation('organizerPanel.cannotChangeOwnerAdmin') || 'Topluluk sahibinin admin durumu değiştirilemez');
      return;
    }
    setAdminModal({ 
      isOpen: true, 
      user, 
      action: user.isAdmin ? 'revoke' : 'assign' 
    });
  };

  const handleAdminConfirm = async () => {
    if (!adminModal.user || !token || !channel?.channelID) return;
    
    try {
      const userID = adminModal.user.userID || adminModal.user.id || '';
      let response;
      
      if (adminModal.action === 'assign') {
        response = await Services.SocialHiveAssignAdminToChannel(token, channel.channelID, userID);
      } else {
        response = await Services.SocialHiveRevokeAdminFromChannel(token, channel.channelID, userID);
      }
      
      if (response.data.status) {
        toastSuccess(
          adminModal.action === 'assign'
            ? (getTranslation('organizerPanel.adminAssigned') || 'Admin yetkisi verildi')
            : (getTranslation('organizerPanel.adminRevoked') || 'Admin yetkisi kaldırıldı')
        );
        await fetchMembers();
        setAdminModal({ isOpen: false, user: null, action: 'assign' });
      } else {
        toastError(getTranslation('organizerPanel.adminActionError') || 'İşlem başarısız');
      }
    } catch (error) {
      toastError(getTranslation('organizerPanel.adminActionError') || 'İşlem başarısız');
    }
  };

  const handleApproveClick = (user: ChannelUser) => {
    setApproveModal({ isOpen: true, user });
  };

  const handleApproveConfirm = async () => {
    if (!approveModal.user || !token || !channel?.channelID) return;
    
    try {
      const response = await Services.SocialHiveAcceptUserToPrivateChannel(
        token,
        channel.channelID,
        approveModal.user.userID || approveModal.user.id || ''
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.userApproved') || 'Kullanıcı onaylandı');
        await Promise.all([fetchMembers(), fetchPendingUsers()]);
        setApproveModal({ isOpen: false, user: null });
      } else {
        toastError(getTranslation('organizerPanel.userApproveError') || 'Kullanıcı onaylanamadı');
      }
    } catch (error) {
      toastError(getTranslation('organizerPanel.userApproveError') || 'Kullanıcı onaylanamadı');
    }
  };

  const handleDenyClick = (user: ChannelUser) => {
    setDenyModal({ isOpen: true, user });
  };

  const handleDenyConfirm = async () => {
    if (!denyModal.user || !token || !channel?.channelID) return;
    
    try {
      const response = await Services.SocialHiveRejectUserToPrivateChannel(
        token,
        channel.channelID,
        denyModal.user.userID || denyModal.user.id || ''
      );
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.userDenied') || 'Kullanıcı reddedildi');
        await fetchPendingUsers();
        setDenyModal({ isOpen: false, user: null });
      } else {
        toastError(getTranslation('organizerPanel.userDenyError') || 'Kullanıcı reddedilemedi');
      }
    } catch (error) {
      toastError(getTranslation('organizerPanel.userDenyError') || 'Kullanıcı reddedilemedi');
    }
  };

  const getUserAvatar = (user: ChannelUser): string | null => {
    return user.userAvatar || user.avatar || user.image || null;
  };

  const getUserName = (user: ChannelUser): string => {
    return `${user.name || ''} ${user.lastname || ''}`.trim() || 'Kullanıcı';
  };

  const getUserEmail = (user: ChannelUser): string | null => {
    return (user as ChannelUser & { email?: string; userEmail?: string }).email ||
      (user as ChannelUser & { email?: string; userEmail?: string }).userEmail || null;
  };

  // Segmentation helpers
  const getMemberTags = (user: ChannelUser): string[] => {
    if (!user.tags || !channel?.channelID) return [];
    return user.tags[channel.channelID] || [];
  };

  const getMemberUserId = (user: ChannelUser): string => {
    return user.userID || user.id || '';
  };

  const handleSelectMember = (userId: string) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAllMembers = () => {
    if (selectedMembers.size === filteredAndSortedMembers.length) {
      setSelectedMembers(new Set());
    } else {
      setSelectedMembers(new Set(filteredAndSortedMembers.map(u => getMemberUserId(u))));
    }
  };

  const handleCreateAndAssignTag = async (tag: string, user: ChannelUser) => {
    const userId = getMemberUserId(user);
    if (!userId) return;

    const created = await addSegment(tag);
    if (!created) return;

    const assigned = await addTagToUsers(tag, [userId]);
    if (assigned) {
      setMembers(prev => prev.map(u => {
        if (getMemberUserId(u) !== userId) return u;
        const currentTags = getMemberTags(u);
        return {
          ...u,
          tags: {
            ...u.tags,
            [channel?.channelID || '']: [...currentTags, tag],
          },
        };
      }));
      toastSuccess(getTranslation('organizerPanel.tagAdded') || 'Tag added');
    }
  };

  const handleMemberTagToggle = async (tag: string, isCurrentlyAssigned: boolean, user: ChannelUser) => {
    const userId = getMemberUserId(user);
    if (!userId) return;

    const success = isCurrentlyAssigned
      ? await removeTagFromUsers(tag, [userId])
      : await addTagToUsers(tag, [userId]);

    if (success) {
      // Update local state optimistically
      setMembers(prev => prev.map(u => {
        if (getMemberUserId(u) !== userId) return u;
        const currentTags = getMemberTags(u);
        const newTags = isCurrentlyAssigned
          ? currentTags.filter(t => t !== tag)
          : [...currentTags, tag];
        return {
          ...u,
          tags: {
            ...u.tags,
            [channel?.channelID || '']: newTags,
          },
        };
      }));
      toastSuccess(
        isCurrentlyAssigned
          ? (getTranslation('organizerPanel.tagRemoved') || 'Tag removed')
          : (getTranslation('organizerPanel.tagAdded') || 'Tag added')
      );
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to update tag');
    }
  };

  const handleBulkAddTag = (tag: string) => {
    const pending = pendingBulkTags[tag];
    if (pending === 'add') {
      // Undo add
      setPendingBulkTags(prev => {
        const next = { ...prev };
        delete next[tag];
        return next;
      });
    } else {
      setPendingBulkTags(prev => ({ ...prev, [tag]: 'add' }));
    }
  };

  const handleBulkRemoveTag = (tag: string) => {
    const pending = pendingBulkTags[tag];
    if (pending === 'remove') {
      // Undo remove
      setPendingBulkTags(prev => {
        const next = { ...prev };
        delete next[tag];
        return next;
      });
    } else {
      setPendingBulkTags(prev => ({ ...prev, [tag]: 'remove' }));
    }
  };

  const handleSaveBulkTags = async () => {
    const userIds = filteredAndSortedMembers
      .filter(u => selectedMembers.has(getMemberUserId(u)))
      .map(u => getMemberUserId(u))
      .filter(Boolean);

    if (userIds.length === 0) return;

    const entries = Object.entries(pendingBulkTags);
    if (entries.length === 0) {
      setShowBulkTagModal(false);
      return;
    }

    let allSuccess = true;
    for (const [tag, action] of entries) {
      const success = action === 'add'
        ? await addTagToUsers(tag, userIds)
        : await removeTagFromUsers(tag, userIds);

      if (success) {
        setMembers(prev => prev.map(u => {
          if (!selectedMembers.has(getMemberUserId(u))) return u;
          const currentTags = getMemberTags(u);

          if (action === 'remove') {
            return {
              ...u,
              tags: {
                ...u.tags,
                [channel?.channelID || '']: currentTags.filter(t => t !== tag),
              },
            };
          } else {
            if (currentTags.includes(tag)) return u;
            return {
              ...u,
              tags: {
                ...u.tags,
                [channel?.channelID || '']: [...currentTags, tag],
              },
            };
          }
        }));
      } else {
        allSuccess = false;
      }
    }

    if (allSuccess) {
      toastSuccess(getTranslation('organizerPanel.tagsUpdated') || 'Tags updated successfully');
    } else {
      toastError(getTranslation('organizerPanel.tagUpdateError') || 'Failed to update tags');
    }

    setPendingBulkTags({});
    setShowBulkTagModal(false);
  };

  const handleCancelBulkTags = () => {
    setPendingBulkTags({});
    setShowBulkTagModal(false);
  };

  // Handle sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Filter and sort members
  const filteredAndSortedMembers = useMemo(() => {
    let result = [...members];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) => {
        const displayName = getUserName(user).toLowerCase();
        const email = (getUserEmail(user) || '').toLowerCase();
        return displayName.includes(query) || email.includes(query);
      });
    }

    // Sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aValue: string | number = '';
        let bValue: string | number = '';

        switch (sortColumn) {
          case 'userName':
            aValue = getUserName(a).toLowerCase();
            bValue = getUserName(b).toLowerCase();
            break;
          case 'memberType':
            const aIsOwner = a.isOwner === true || (a as ChannelUser & { isOwner?: "1" | 1 }).isOwner === "1" || (a as ChannelUser & { isOwner?: "1" | 1 }).isOwner === 1;
            const bIsOwner = b.isOwner === true || (b as ChannelUser & { isOwner?: "1" | 1 }).isOwner === "1" || (b as ChannelUser & { isOwner?: "1" | 1 }).isOwner === 1;
            const aIsAdmin = a.isAdmin === true || (a as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === "1" || (a as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === 1;
            const bIsAdmin = b.isAdmin === true || (b as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === "1" || (b as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === 1;
            
            // Sort: Owner (0) > Admin (1) > Member (2)
            const aType = aIsOwner ? 0 : aIsAdmin ? 1 : 2;
            const bType = bIsOwner ? 0 : bIsAdmin ? 1 : 2;
            if (aType !== bType) {
              return sortDirection === 'asc' ? aType - bType : bType - aType;
            }
            // If same type, sort by name
            aValue = getUserName(a).toLowerCase();
            bValue = getUserName(b).toLowerCase();
            break;
          default:
            return 0;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return result;
  }, [members, searchQuery, sortColumn, sortDirection]);

  // Filter pending users based on search
  const filteredPendingUsers = useMemo(() => {
    let result = [...pendingUsers];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((user) => {
        const displayName = getUserName(user).toLowerCase();
        const email = (getUserEmail(user) || '').toLowerCase();
        return displayName.includes(query) || email.includes(query);
      });
    }
    
    return result;
  }, [pendingUsers, searchQuery]);

  return (
    <div className={styles.communityMembersPage}>
      <div className={styles.header}>
        <Tabs variant="default">
          <Tab
            variant="default"
            isActive={activeTab === 'members'}
            onClick={() => {
              setActiveTab('members');
              setSearchQuery('');
              setSortColumn(null);
              setSortDirection(null);
            }}
          >
            {getTranslation('organizerPanel.members') || 'Üyeler'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'requests'}
            onClick={() => {
              setActiveTab('requests');
              setSearchQuery('');
              setSortColumn(null);
              setSortDirection(null);
            }}
          >
            {getTranslation('organizerPanel.pendingRequests') || 'Bekleyen İstekler'}
          </Tab>
        </Tabs>
        {activeTab === 'members' && (
          <div className={styles.searchContainer}>
            <FormInput
              type="text"
              placeholder={getTranslation('table.searchMembers') || 'Search members...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              disabled={loading}
            />
          </div>
        )}
        {activeTab === 'requests' && (
          <div className={styles.searchContainer}>
            <FormInput
              type="text"
              placeholder={getTranslation('table.searchRequests') || 'Search requests...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
              disabled={loading}
            />
          </div>
        )}
      </div>

      <div key={activeTab} className={transitionStyles.fadeIn}>
      {activeTab === 'members' ? (
        <div className={styles.membersList}>
          <Table>
            <div className={styles.tableHeaderWrapper}>
              <div className={styles.checkboxHeader}>
                <input
                  type="checkbox"
                  checked={selectedMembers.size === filteredAndSortedMembers.length && filteredAndSortedMembers.length > 0}
                  onChange={handleSelectAllMembers}
                  disabled={loading || filteredAndSortedMembers.length === 0}
                  className={styles.checkbox}
                />
              </div>
              <TableHeader
                columns={[
                  getTranslation('organizerPanel.userName') || 'Kullanıcı Adı',
                  getTranslation('organizerPanel.tags') || 'Tags',
                  getTranslation('organizerPanel.memberType') || 'Üye Tipi',
                  getTranslation('organizerPanel.admin') || 'Yönetici',
                  getTranslation('organizerPanel.actions') || 'İşlemler'
                ]}
                columnWidths={['2fr', '1fr', '120px', '80px', '80px']}
                columnKeys={['userName', 'tags', 'memberType', null, null]}
                sortableColumns={['userName', 'memberType']}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </div>
            <TableBody>
              {loading ? (
                // Skeleton loading rows
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={`skeleton-${index}`} className={styles.tableRowWrapper}>
                    <div className={styles.checkboxCell}>
                      <LoadingSkeleton height={18} width={18} />
                    </div>
                    <TableRow columnWidths={['2fr', '1fr', '120px', '80px', '80px']}>
                      <TableCell>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <LoadingSkeleton height={40} width={40} variant="circular" />
                          <div style={{ flex: 1 }}>
                            <LoadingSkeleton height={16} width="60%" />
                            <div style={{ marginTop: '4px' }}>
                              <LoadingSkeleton height={14} width="80%" />
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <LoadingSkeleton height={20} width="60%" />
                      </TableCell>
                      <TableCell>
                        <LoadingSkeleton height={16} width="70%" />
                      </TableCell>
                      <TableCell>
                        <LoadingSkeleton height={20} width={20} variant="rectangular" />
                      </TableCell>
                      <TableCell>
                        <LoadingSkeleton height={32} width={32} variant="rectangular" />
                      </TableCell>
                    </TableRow>
                  </div>
                ))
              ) : filteredAndSortedMembers.length === 0 ? (
                <EmptyTableRow message={getTranslation('organizerPanel.noMembers') || 'Üye bulunamadı'} />
              ) : (
                filteredAndSortedMembers.map((user) => {
                  const avatarUrl = getUserAvatar(user);
                  const displayName = getUserName(user);
                  const userEmail = getUserEmail(user);
                  const isOwner = user.isOwner === true || (user as ChannelUser & { isOwner?: "1" | 1 }).isOwner === "1" || (user as ChannelUser & { isOwner?: "1" | 1 }).isOwner === 1;
                  const isAdmin = user.isAdmin === true || (user as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === "1" || (user as ChannelUser & { isAdmin?: "1" | 1 }).isAdmin === 1;
                  const memberTags = getMemberTags(user);
                  const userId = getMemberUserId(user);
                  const isSelected = selectedMembers.has(userId);

                  return (
                    <div key={user.id || user.userID} className={`${styles.tableRowWrapper} ${isSelected ? styles.tableRowSelected : ''}`}>
                      <div className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleSelectMember(userId)}
                          className={styles.checkbox}
                        />
                      </div>
                      <TableRow
                        columnWidths={['2fr', '1fr', '120px', '80px', '80px']}
                      >
                        <TableCell>
                          <UserProfileLink
                            userId={user.userID || user.id}
                            name={user.name}
                            lastname={user.lastname}
                          >
                            <UserCard
                              avatarUrl={avatarUrl}
                              name={displayName}
                              email={userEmail}
                              size={40}
                            />
                          </UserProfileLink>
                        </TableCell>
                        <TableCell>
                          <span
                            ref={activeTagDropdown === userId ? tagDropdownAnchorRef : undefined}
                          >
                            <UserTagsDisplay
                              tags={memberTags}
                              onClick={() => setActiveTagDropdown(activeTagDropdown === userId ? null : userId)}
                              emptyText={getTranslation('organizerPanel.noTags') || 'No tags'}
                            />
                          </span>
                          {activeTagDropdown === userId && (
                            <TagDropdown
                              isOpen={true}
                              onClose={() => setActiveTagDropdown(null)}
                              availableTags={segments}
                              assignedTags={memberTags}
                              onTagToggle={(tag, isAssigned) => handleMemberTagToggle(tag, isAssigned, user)}
                              onCreateTag={(tag) => handleCreateAndAssignTag(tag, user)}
                              anchorRef={tagDropdownAnchorRef}
                              emptyMessage={getTranslation('organizerPanel.noSegments') || 'No segments'}
                              createPlaceholder={getTranslation('organizerPanel.createNewTag') || 'Create new tag...'}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <p className={styles.memberType}>
                            {isOwner
                              ? getTranslation('organizerPanel.owner') || 'Sahip'
                              : isAdmin
                              ? getTranslation('organizerPanel.admin') || 'Yönetici'
                              : getTranslation('organizerPanel.member') || 'Üye'}
                          </p>
                        </TableCell>
                        <TableCell>
                          <label className={styles.checkboxToggleLabel}>
                            <input
                              type="checkbox"
                              className={styles.checkboxToggle}
                              checked={isAdmin}
                              onChange={() => handleAdminToggle(user)}
                              disabled={isOwner}
                              title={isOwner ? (getTranslation('organizerPanel.cannotChangeOwnerAdmin') || 'Sahip durumu değiştirilemez') : ''}
                            />
                          </label>
                        </TableCell>
                        <TableCell>
                          <button
                            className={styles.removeButton}
                            onClick={() => handleRemoveClick(user)}
                            disabled={isOwner}
                            title={isOwner ? (getTranslation('organizerPanel.cannotRemoveOwner') || 'Sahip kaldırılamaz') : (getTranslation('organizerPanel.removeUser') || 'Kullanıcıyı Kaldır')}
                          >
                            {/* @ts-ignore - react-icons compatibility with React 19 */}
                            <HiTrash size={16} />
                          </button>
                        </TableCell>
                      </TableRow>
                    </div>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className={styles.membersList}>
          <Table>
            <TableHeader
              columns={[
                getTranslation('organizerPanel.userName') || 'Kullanıcı Adı',
                getTranslation('organizerPanel.actions') || 'İşlemler'
              ]}
              columnWidths={['1fr', 'auto']}
              columnKeys={['userName', null]}
              sortableColumns={['userName']}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <TableBody>
              {loading ? (
                // Skeleton loading rows
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`} columnWidths={['1fr', 'auto']}>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <LoadingSkeleton height={40} width={40} variant="circular" />
                        <div style={{ flex: 1 }}>
                          <LoadingSkeleton height={16} width="60%" />
                          <div style={{ marginTop: '4px' }}>
                            <LoadingSkeleton height={14} width="80%" />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <LoadingSkeleton height={32} width={80} variant="rectangular" />
                        <LoadingSkeleton height={32} width={80} variant="rectangular" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredPendingUsers.length === 0 ? (
                <EmptyTableRow message={getTranslation('organizerPanel.noPendingRequests') || 'Bekleyen istek bulunamadı'} />
              ) : (
                filteredPendingUsers.map((user) => {
                  const avatarUrl = getUserAvatar(user);
                  const displayName = getUserName(user);

                  return (
                    <TableRow
                      key={user.id || user.userID}
                      columnWidths={['1fr', 'auto']}
                    >
                      <TableCell>
                        <UserProfileLink
                          userId={user.userID || user.id}
                          name={user.name}
                          lastname={user.lastname}
                        >
                          <UserCard
                            avatarUrl={avatarUrl}
                            name={displayName}
                            size={40}
                          />
                        </UserProfileLink>
                      </TableCell>
                      <TableCell>
                        <div className={styles.actionButtons}>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => handleApproveClick(user)}
                            className={styles.approveButton}
                          >
                            {getTranslation('organizerPanel.approve') || 'Onayla'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => handleDenyClick(user)}
                            className={styles.denyButton}
                          >
                            {getTranslation('organizerPanel.deny') || 'Reddet'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
      </div>

      {/* Remove Modal */}
      <Modal
        isOpen={removeModal.isOpen}
        onClose={() => setRemoveModal({ isOpen: false, user: null })}
        onConfirm={handleRemoveConfirm}
        variant="confirm"
        title={getTranslation('organizerPanel.removeUser') || 'Kullanıcıyı Kaldır'}
        message={(getTranslation('organizerPanel.removeUserMessage') || `"${getUserName(removeModal.user || {})}" kullanıcısını topluluktan kaldırmak istediğinizden emin misiniz?`).replace('{userName}', getUserName(removeModal.user || {}))}
        confirmText={getTranslation('organizerPanel.remove') || 'Kaldır'}
        confirmVariant="secondary"
        cancelText={getTranslation('common.cancel') || 'İptal'}
        isDanger={true}
      />

      {/* Admin Toggle Modal */}
      <Modal
        isOpen={adminModal.isOpen}
        onClose={() => setAdminModal({ isOpen: false, user: null, action: 'assign' })}
        onConfirm={handleAdminConfirm}
        variant="confirm"
        title={
          adminModal.action === 'assign'
            ? (getTranslation('organizerPanel.assignAdmin') || 'Admin Yetkisi Ver')
            : (getTranslation('organizerPanel.revokeAdmin') || 'Admin Yetkisini Kaldır')
        }
        message={
          adminModal.action === 'assign'
            ? (getTranslation('organizerPanel.assignAdminMessage') || `"${getUserName(adminModal.user || {})}" kullanıcısına admin yetkisi vermek istediğinizden emin misiniz?`).replace('{userName}', getUserName(adminModal.user || {}))
            : (getTranslation('organizerPanel.revokeAdminMessage') || `"${getUserName(adminModal.user || {})}" kullanıcısının admin yetkisini kaldırmak istediğinizden emin misiniz?`).replace('{userName}', getUserName(adminModal.user || {}))
        }
        confirmText={
          adminModal.action === 'assign'
            ? (getTranslation('organizerPanel.assign') || 'Yetki Ver')
            : (getTranslation('organizerPanel.revoke') || 'Yetkiyi Kaldır')
        }
        confirmVariant="primary"
        cancelText={getTranslation('common.cancel') || 'İptal'}
      />

      {/* Approve Modal */}
      <Modal
        isOpen={approveModal.isOpen}
        onClose={() => setApproveModal({ isOpen: false, user: null })}
        onConfirm={handleApproveConfirm}
        variant="confirm"
        title={getTranslation('organizerPanel.approveUser') || 'Kullanıcıyı Onayla'}
        message={(getTranslation('organizerPanel.approveUserMessage') || `"${getUserName(approveModal.user || {})}" kullanıcısının topluluğa katılma isteğini onaylamak istediğinizden emin misiniz?`).replace('{userName}', getUserName(approveModal.user || {}))}
        confirmText={getTranslation('organizerPanel.approve') || 'Onayla'}
        confirmVariant="primary"
        cancelText={getTranslation('common.cancel') || 'İptal'}
      />

      {/* Deny Modal */}
      <Modal
        isOpen={denyModal.isOpen}
        onClose={() => setDenyModal({ isOpen: false, user: null })}
        onConfirm={handleDenyConfirm}
        variant="confirm"
        title={getTranslation('organizerPanel.denyUser') || 'Kullanıcıyı Reddet'}
        message={(getTranslation('organizerPanel.denyUserMessage') || `"${getUserName(denyModal.user || {})}" kullanıcısının topluluğa katılma isteğini reddetmek istediğinizden emin misiniz?`).replace('{userName}', getUserName(denyModal.user || {}))}
        confirmText={getTranslation('organizerPanel.deny') || 'Reddet'}
        confirmVariant="secondary"
        cancelText={getTranslation('common.cancel') || 'İptal'}
        isDanger={true}
      />

      {/* Bulk Tag Modal */}
      <Modal
        isOpen={showBulkTagModal}
        onClose={handleCancelBulkTags}
        title={getTranslation('organizerPanel.manageTags') || 'Manage Tags'}
        variant="default"
      >
        <div className={styles.bulkTagModalContent}>
          {segments.length === 0 ? (
            <div className={styles.bulkTagEmptyMessage}>
              {getTranslation('organizerPanel.noSegments') || 'No segments created yet'}
            </div>
          ) : (
            <>
              <div className={styles.bulkTagList}>
                {segments.map((tag) => {
                  const pending = pendingBulkTags[tag];
                  const selectedUsers = filteredAndSortedMembers.filter(u => selectedMembers.has(getMemberUserId(u)));
                  const usersWithTag = selectedUsers.filter(u => getMemberTags(u).includes(tag)).length;
                  const usersWithoutTag = selectedUsers.length - usersWithTag;
                  return (
                    <div key={tag}>
                      <div className={styles.bulkTagItem}>
                        <div className={styles.bulkTagItemLabel}>
                          <UserTagsDisplay tags={[tag]} maxVisible={1} />
                        </div>
                        <div className={styles.bulkTagItemActions}>
                          <button
                            type="button"
                            className={`${styles.bulkTagActionBtn} ${pending === 'remove' ? styles.removeActive : ''}`}
                            onClick={() => handleBulkRemoveTag(tag)}
                            title={getTranslation('organizerPanel.removeTagFromUsers') || 'Remove from selected users'}
                          >
                            {pending === 'remove' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            className={`${styles.bulkTagActionBtn} ${pending === 'add' ? styles.addActive : ''}`}
                            onClick={() => handleBulkAddTag(tag)}
                            title={getTranslation('organizerPanel.addTagToUsers') || 'Add to selected users'}
                          >
                            {pending === 'add' ? (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      {pending === 'add' && (() => {
                        const parts = (getTranslation('organizerPanel.tagWillBeAdded') || 'Will be added to {count} users who don\'t have it').split('{count}');
                        return (
                          <div className={`${styles.bulkTagWarning} ${styles.bulkTagWarningAdd}`}>
                            {parts[0]}<strong>{usersWithoutTag}{parts[1]}</strong>
                          </div>
                        );
                      })()}
                      {pending === 'remove' && (() => {
                        const parts = (getTranslation('organizerPanel.tagWillBeRemoved') || 'Will be removed from {count} users who have it').split('{count}');
                        return (
                          <div className={`${styles.bulkTagWarning} ${styles.bulkTagWarningRemove}`}>
                            {parts[0]}<strong>{usersWithTag}{parts[1]}</strong>
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
              <div className={styles.bulkTagActions}>
                <Button variant="secondary" size="small" onClick={handleCancelBulkTags}>
                  {getTranslation('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSaveBulkTags}
                  disabled={Object.keys(pendingBulkTags).length === 0}
                >
                  {getTranslation('common.save') || 'Save'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Floating Bulk Selection Action Bar */}
      {activeTab === 'members' && selectedMembers.size > 0 && (
        <div className={styles.bulkActionBar}>
          <span className={styles.bulkActionText}>
            {(getTranslation('organizerPanel.selectedUsers') || '{count} selected').replace('{count}', String(selectedMembers.size))}
          </span>
          <Button
            variant="ghost"
            size="small"
            className={styles.clearButton}
            onClick={() => setSelectedMembers(new Set())}
          >
            {getTranslation('common.clearSelection') || 'Clear'}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => { setPendingBulkTags({}); setShowBulkTagModal(true); }}
          >
            {/* @ts-ignore */}
            <HiTag size={16} />
            {getTranslation('organizerPanel.manageTags') || 'Manage Tags'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommunityMembersPage;

